import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Globaler Error-Boundary für die Admin-UI. Fängt Render-Fehler ab,
 * damit ein Bug in einer Page nicht die gesamte App lahmlegt. Bietet
 * einen "Reload"-Knopf und zeigt im Dev-Modus den Stack.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  private handleReload = (): void => {
    this.setState({ error: null });
    window.location.reload();
  };

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg p-6">
        <div className="max-w-lg w-full card p-6 space-y-3">
          <h1 className="text-lg font-semibold text-text">Da ist etwas schiefgelaufen</h1>
          <p className="text-sm text-text-mute">{this.state.error.message}</p>
          {import.meta.env.DEV && this.state.error.stack && (
            <pre className="text-xs text-text-mute whitespace-pre-wrap overflow-auto max-h-64 bg-surface-2 p-2 rounded">
              {this.state.error.stack}
            </pre>
          )}
          <button
            className="mt-2 px-3 py-1 rounded bg-accent text-bg text-sm hover:opacity-90"
            onClick={this.handleReload}
          >
            Neu laden
          </button>
        </div>
      </div>
    );
  }
}
